import {
  DocsCallout,
  DocsContent,
  DocsLayout,
  DocsLink,
  DocsScreenshot,
  H2,
  H3,
} from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsGraphExplorer() {
  return (
    <DocsLayout>
      <DocsContent
        title="Graph Explorer"
        description="Master the knowledge graph visualization to discover connections, trace relationships, and uncover patterns in your investigation data."
        previousPage={{ title: 'Geospatial Pipeline', href: '/docs/pipelines/geospatial' }}
        nextPage={{ title: 'Natural Language Queries', href: '/docs/natural-language' }}
      >
        <H2 id="understanding-the-graph">Understanding the Graph</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The knowledge graph is a visual representation of all entities extracted from your
          documents and their relationships. It uses a force-directed layout where:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Nodes</strong> represent entities (people,
              organizations, locations, etc.)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Edges</strong> represent relationships between
              entities
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Clusters</strong> form naturally around highly
              connected entities
            </span>
          </li>
        </ul>

        <DocsScreenshot
          src="/docs/images/graph/graph-explorer.svg"
          alt="Knowledge Graph Overview"
          caption="The knowledge graph showing entities and their interconnections"
        />

        <H2 id="entity-types-and-colors">Entity Types and Colors</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Nodes are color-coded by entity type for quick visual identification:
        </p>

        <div className="grid grid-cols-2 gap-3 my-6">
          <EntityTypeCard
            color="blue"
            label="Person"
            description="Individuals mentioned in documents"
          />
          <EntityTypeCard
            color="purple"
            label="Organization"
            description="Companies, agencies, groups"
          />
          <EntityTypeCard
            color="green"
            label="Location"
            description="Addresses, cities, countries"
          />
          <EntityTypeCard
            color="amber"
            label="Document"
            description="Source documents in the case"
          />
          <EntityTypeCard color="cyan" label="Phone" description="Phone numbers" />
          <EntityTypeCard color="pink" label="Email" description="Email addresses" />
          <EntityTypeCard color="red" label="Vehicle" description="Cars, boats, aircraft" />
          <EntityTypeCard color="gray" label="Other" description="Miscellaneous entities" />
        </div>

        <H2 id="graph-navigation">Graph Navigation</H2>

        <H3 id="zoom-and-pan">Zoom and Pan</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Scroll</strong> to zoom in and out
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Click and drag</strong> on empty space to pan the
              view
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Double-click</strong> on empty space to reset the
              view
            </span>
          </li>
        </ul>

        <H3 id="selecting-nodes">Selecting Nodes</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Click</strong> on a node to select it and see its
              details
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Double-click</strong> on a node to expand its
              connections
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Drag</strong> a node to reposition it manually
            </span>
          </li>
        </ul>

        <DocsCallout variant="tip" title="Keyboard Shortcuts">
          Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Escape</kbd> to deselect all
          nodes. Use <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">F</kbd>
          to fit the entire graph in view.
        </DocsCallout>

        <H2 id="entity-details-panel">Entity Details Panel</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          When you select a node, the details panel on the right shows:
        </p>

        <H3 id="properties">Properties</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          All extracted information about the entity, such as:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Names and aliases</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Contact information</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Dates and locations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Document-specific details</span>
          </li>
        </ul>

        <H3 id="connections">Connections</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          A list of all entities connected to the selected node, grouped by relationship type. Click
          any connection to navigate to that entity.
        </p>

        <H3 id="source-documents">Source Documents</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          The documents where this entity was mentioned. Click to view the original document with
          the relevant sections highlighted.
        </p>

        <H2 id="structured-view-vs-graph-view">Structured View vs Graph View</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          XENDREL offers two ways to explore your data:
        </p>

        <H3 id="graph-view">Graph View</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          The visual network diagram. Best for:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Discovering unexpected connections</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Understanding relationship patterns</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Visual exploration of clusters</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Presentations and reports</span>
          </li>
        </ul>

        <H3 id="structured-view">Structured View (Entities Explorer)</H3>
        <p className="text-foreground mb-4 leading-relaxed">A table-based interface. Best for:</p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Browsing large numbers of entities</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Filtering by specific criteria</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Exporting entity lists</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Detailed property comparison</span>
          </li>
        </ul>

        <DocsScreenshot
          src="/docs/images/graph/structured-view.svg"
          alt="Structured Entities Explorer"
          caption="The Entities Explorer showing a filterable table of all entities"
        />

        <p className="text-foreground mb-4 leading-relaxed">
          Switch between views using the dropdown in the toolbar that shows "Main Graph" or
          "Entities Explorer".
        </p>

        <H2 id="filtering-the-graph">Filtering the Graph</H2>

        <H3 id="entity-type-filters">Entity Type Filters</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Use the filter panel to show or hide specific entity types. This helps focus on relevant
          connections without visual clutter.
        </p>

        <H3 id="search-filtering">Search Filtering</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Type in the search box to filter entities by name. Matching entities are highlighted, and
          you can click results to navigate directly to them.
        </p>

        <H2 id="path-finding">Path Finding</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Discover how two entities are connected, even through multiple intermediaries.
        </p>

        <DocsCallout variant="info" title="How to Find Paths">
          <ol className="list-decimal list-inside space-y-1 mt-2">
            <li>Select the starting entity</li>
            <li>Click the "Find Path" button in the details panel</li>
            <li>Click on the destination entity</li>
            <li>The shortest path is highlighted on the graph</li>
          </ol>
        </DocsCallout>

        <p className="text-foreground mb-4 leading-relaxed">Path finding is invaluable for:</p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Tracing relationships between suspects</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Finding intermediaries in transactions</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Understanding organizational hierarchies</span>
          </li>
        </ul>

        <H2 id="sandboxes">Sandboxes</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Sandboxes let you create focused subgraphs for specific analysis without affecting the
          main graph.
        </p>

        <H3 id="creating-a-sandbox">Creating a Sandbox</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Click the graph dropdown and select "Create New Sandbox"</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Give it a descriptive name (e.g., "Financial Network Analysis")</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              Add entities from the main graph by selecting them and clicking "Add to Sandbox"
            </span>
          </li>
        </ul>

        <H3 id="sandbox-use-cases">Sandbox Use Cases</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Isolate a specific cluster for detailed analysis</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Create presentation-ready diagrams</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Compare different subsets of your data</span>
          </li>
        </ul>

        <H2 id="identity-management">Identity Management</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Identity panel helps you manage and verify person identities extracted from documents.
          Access it by clicking the <strong className="text-foreground">Identity</strong> button in
          the toolbar.
        </p>

        <H3 id="identity-overview">Identity Overview</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          The panel shows statistics about identities in your case:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Total Identities</strong> - All person nodes in
              the graph
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Verified Documents</strong> - Persons with
              confirmed identity documents
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Possible Documents</strong> - Persons with
              potential document matches
            </span>
          </li>
        </ul>

        <H3 id="viewing-identity-details">Viewing Identity Details</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Click on any person in the list to view their full identity profile, including:
        </p>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Personal information (name, date of birth, nationality)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Associated identity documents (passports, IDs)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Document photos and extracted data</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Connections to other entities</span>
          </li>
        </ul>

        <H2 id="geolocation-map">Geolocation Map</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Geolocation panel displays extracted locations on an interactive map. Access it by
          clicking the <strong className="text-foreground">Geolocation</strong> button in the
          toolbar.
        </p>

        <H3 id="map-features">Map Features</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Location markers</strong> - Places mentioned in
              documents
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity positions</strong> - Where persons and
              vehicles were located
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>
                <strong className="text-foreground">Timeline view</strong> - See location changes
                over time
              </span>
              <DocsLink
                to="/docs/timeline-view"
                hint="The Timeline view synchronises the knowledge graph, map, and events list against a scrubbable playhead and time-range filter."
              />
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Connection lines</strong> - Visual links between
              related locations
            </span>
          </li>
        </ul>

        <H3 id="map-navigation">Map Navigation</H3>
        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Scroll</strong> to zoom in and out
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Drag</strong> to pan the map
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Click markers</strong> to see location details
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Filter by entity type</strong> to focus on
              specific locations
            </span>
          </li>
        </ul>

        <H2 id="graph-controls">Graph Controls</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The toolbar provides quick access to common actions:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Refresh</strong> - Reload the graph with the
              latest data
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Clear Graph</strong> - Remove all nodes from the
              current view
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Chat</strong> - Open the natural language query
              panel
            </span>
          </li>
        </ul>

        <DocsCallout variant="warning" title="Clear Graph">
          Clearing the graph only affects your current view. It doesn't delete any data - you can
          always refresh to see all entities again.
        </DocsCallout>
      </DocsContent>
    </DocsLayout>
  )
}

function EntityTypeCard({
  color,
  label,
  description,
}: {
  color: string
  label: string
  description: string
}) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    cyan: 'bg-cyan-500',
    pink: 'bg-pink-500',
    red: 'bg-red-500',
    gray: 'bg-gray-500',
  }

  return (
    <Card size="compact" className="flex flex-row items-center gap-3 rounded-lg p-3 bg-card/50">
      <span className={`w-4 h-4 rounded-full ${colorClasses[color]}`}></span>
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
    </Card>
  )
}
